import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DefaultModalProps } from './default-modal.component';

interface ViewProps {
    modalType: string;
    width: any;
    modalRef: (el: HTMLDivElement | null) => void;
}

const useDefaultModal = (props: DefaultModalProps): ViewProps => {
    const internalRef = useRef<HTMLDivElement | null>(null);
    const modalType = props.type ? 'modal-' + props.type.valueOf() : 'modal-prompt';
    const width = props.customWidth !== undefined ? { width: props.customWidth + '%' } : {};
    const [queryParams, setQueryParams] = useSearchParams();

    // Use the internal ref for click-outside detection
    // The actual ref assigned to DOM is handled via props.modalRef callback
    const getModalElement = (): HTMLElement | null => {
        return internalRef.current;
    };

    useEffect(() => {
        const isTopmostModal = (): boolean => {
            const backdrops = document.querySelectorAll('[data-dialog-backdrop="modal"]');
            if (backdrops.length === 0) return true;
            const topBackdrop = backdrops[backdrops.length - 1];
            // Each DefaultModal backdrop id is `default-modal-${props.id}`
            return topBackdrop.id === `default-modal-${props.id}`;
        };

        const handleClickOutside = (event: { target: any }) => {
            if (event.target.closest('.ignore-modal-close')) return;
            // Nested modals each register a document listener; only the topmost
            // should close on outside click (otherwise parents close with the child).
            if (!isTopmostModal()) return;

            const modalElement = getModalElement();
            if (queryParams.has('details')) {
                const details = queryParams.get('details');
                if (!details || !props.id) return;

                const detailsArray = details.slice(1, -1).split(',');
                const lastDetail = detailsArray[detailsArray.length - 1];
                const newArray = lastDetail.slice(1, -1).split('-');
                newArray.shift();

                if (newArray.join('-') === props.id) {
                    if (modalElement && !modalElement.contains(event.target)) {
                        props.show && props.handleClose && props.handleClose();
                    }
                }
            } else {
                if (modalElement && !modalElement.contains(event.target)) {
                    props.show && props.handleClose && props.handleClose();
                }
            }
        };
        document.addEventListener('mousedown', props.handleClickOutside || handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', props.handleClickOutside || handleClickOutside, true);
        };
    }, [internalRef, props.handleClose, props.id, props.show, queryParams]);

    // Create a combined ref callback that updates both the internal ref and props.modalRef
    const combinedModalRef = (el: HTMLDivElement | null) => {
        internalRef.current = el;
        if (props.modalRef) {
            props.modalRef(el);
        }
    };

    return {
        modalType,
        width,
        modalRef: combinedModalRef
    };
};

export default useDefaultModal;
